import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useAdminLanguage } from '../AdminLanguageContext';
import useAdminDialog from '../components/AdminDialog';

// 敏感词列表
const SENSITIVE = ['垃圾', '骗子', '欺诈', 'shit', 'fuck'];

function highlightSensitive(text) {
  if (!text) return text;
  let parts = [text];
  SENSITIVE.forEach(word => {
    parts = parts.flatMap(p => typeof p === 'string'
      ? p.split(new RegExp(`(${word})`, 'gi')).map((seg, i) => seg.toLowerCase() === word.toLowerCase()
          ? <mark key={word + i} style={{ background: '#fee', color: '#c33', padding: '0 2px' }}>{seg}</mark>
          : seg)
      : [p]);
  });
  return parts;
}

export default function Reviews() {
  const { copy } = useAdminLanguage();
  const reviewsCopy = copy.reviews;
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [keyword, setKeyword] = useState('');
  const { confirmAction, dialog } = useAdminDialog();

  const fetchReviews = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/admin/reviews', { params: { rating: ratingFilter, keyword } })
      .then(res => setReviews(res.data.reviews || []))
      .catch(err => setError(err.response?.data?.error || reviewsCopy.errors.loadFailed))
      .finally(() => setLoading(false));
  }, [ratingFilter, keyword, reviewsCopy.errors.loadFailed]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const hasSensitive = (text = '') => SENSITIVE.some(w => text.toLowerCase().includes(w.toLowerCase()));

  const filtered = reviews.filter(r => {
    if (ratingFilter !== 'all' && ratingFilter !== 'sensitive' && r.rating !== Number(ratingFilter)) return false;
    if (ratingFilter === 'sensitive' && !hasSensitive(r.comment)) return false;
    if (keyword.trim()) {
      const q = keyword.trim().toLowerCase();
      return (r.comment || '').toLowerCase().includes(q) ||
        (r.reviewer_name || '').toLowerCase().includes(q) ||
        (r.reviewee_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const handleDelete = async (id) => {
    if (!await confirmAction({
      title: reviewsCopy.deleteConfirm.title,
      message: reviewsCopy.deleteConfirm.message,
      variant: 'danger',
    })) return;
    await api.delete(`/admin/reviews/${id}`);
    fetchReviews();
  };

  const avg = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(2)
    : 0;
  const sensitiveCount = reviews.filter(r => hasSensitive(r.comment)).length;

  return (
    <div>
      {dialog}
      <div className="page-title">{reviewsCopy.title}</div>
      {error && <div className="card" style={{ color: '#b91c1c', marginBottom: 16 }}>{error}</div>}

      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <div className="card stat-card"><div className="stat-label">{reviewsCopy.stats.total}</div><div className="stat-value">{reviews.length}</div></div>
        <div className="card stat-card"><div className="stat-label">{reviewsCopy.stats.average}</div><div className="stat-value" style={{ color: '#ffa500' }}>⭐ {avg}</div></div>
        <div className="card stat-card"><div className="stat-label">{reviewsCopy.stats.fiveStar}</div><div className="stat-value" style={{ color: '#1dbf73' }}>{reviews.filter(r => r.rating === 5).length}</div></div>
        <div className="card stat-card"><div className="stat-label">{reviewsCopy.stats.sensitive}</div><div className="stat-value" style={{ color: '#ef4444' }}>{sensitiveCount}</div></div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div className="search-input">
            <input placeholder={reviewsCopy.searchPlaceholder} value={keyword} onChange={e => setKeyword(e.target.value)} />
          </div>
          <select value={ratingFilter} onChange={e => setRatingFilter(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <option value="all">{reviewsCopy.ratingFilter.all}</option>
            <option value="5">{reviewsCopy.ratingFilter.five}</option>
            <option value="4">{reviewsCopy.ratingFilter.four}</option>
            <option value="3">{reviewsCopy.ratingFilter.three}</option>
            <option value="2">{reviewsCopy.ratingFilter.two}</option>
            <option value="1">{reviewsCopy.ratingFilter.one}</option>
            <option value="sensitive">{reviewsCopy.ratingFilter.sensitive}</option>
          </select>
          <span style={{ fontSize: 13, color: '#6b7280', alignSelf: 'center', marginLeft: 'auto' }}>{reviewsCopy.showing(filtered.length)}</span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{reviewsCopy.columns.id}</th><th>{reviewsCopy.columns.task}</th><th>{reviewsCopy.columns.reviewer}</th><th>{reviewsCopy.columns.reviewee}</th><th>{reviewsCopy.columns.rating}</th><th>{reviewsCopy.columns.comment}</th><th>{reviewsCopy.columns.createdAt}</th><th>{reviewsCopy.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{reviewsCopy.loading}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{reviewsCopy.empty}</td></tr>
              ) : filtered.map(r => {
                const isSensitive = hasSensitive(r.comment);
                return (
                  <tr key={r.id} style={isSensitive ? { background: '#fff5f5' } : {}}>
                    <td>{r.id}</td>
                    <td>{r.task_title || '-'}</td>
                    <td>{r.reviewer_name || '-'}</td>
                    <td style={{ fontWeight: 600 }}>{r.reviewee_name || '-'}</td>
                    <td>
                      {[...Array(5)].map((_, i) => (
                        <span key={i} style={{ color: i < r.rating ? '#ffa500' : '#ddd' }}>★</span>
                      ))}
                    </td>
                    <td style={{ maxWidth: 280 }}>{highlightSensitive(r.comment)}</td>
                    <td style={{ color: '#6b7280' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r.id)}>{reviewsCopy.actions.delete}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
